import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { getOrder, setOrderLabel, type OrderDetail } from '@/api/orders';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { PageHeader } from '@/components/PageHeader';
import { VendorMark } from '@/components/VendorMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { nextOrderAction } from '@/features/orders/orderPresentation';
import { MarkOrderedForm } from '@/features/orders/journey/MarkOrderedForm';
import { ActivityTab } from '@/features/orders/journey/ActivityTab';
import { DocumentsTab } from '@/features/orders/journey/DocumentsTab';
import { FactsStrip } from '@/features/orders/journey/FactsStrip';
import { ItemsTab } from '@/features/orders/journey/ItemsTab';
import { OrderPrepDetail } from '@/features/orders/OrderPrepSection';
import { useAuth } from '@/auth/useAuth';
import { JourneyActions } from '@/features/orders/journey/JourneyActions';
import { JourneyRail } from '@/features/orders/journey/JourneyRail';
import { NotesTab } from '@/features/orders/journey/NotesTab';
import { ReceivingTab } from '@/features/orders/journey/ReceivingTab';
import { WORKSPACE_TABS, type WorkspaceTab } from '@/features/orders/journey/tabs';
import { PageState } from './PageState';

/**
 * One journey. The anatomy is fixed for every journey: header with the one
 * next action and an overflow menu, the facts strip, then the tabs. Reading
 * is the job here; the WORK views stay the bulk-action surfaces.
 */
export function OrderWorkspacePage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { overview, load, patchOrder, lastLoadedAt } = useOrdersDataStore();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  // Draft is the prep run's proposal: a tab while the journey is still to be
  // ordered (or when a link asks for it), for whoever may read drafts.
  const canDrafts = useAuth().can('orders:read:drafts');
  const requested = searchParams.get('tab');
  const tabs = WORKSPACE_TABS.filter(([key]) => key !== 'draft' || (canDrafts && (detail?.status === 'announced' || requested === 'draft')));
  const tab: WorkspaceTab = tabs.find(([key]) => key === requested)?.[0] || 'summary';
  const stateReturn = location.state?.returnTo;
  const returnTo = typeof stateReturn === 'string' && stateReturn.startsWith('/') && !stateReturn.startsWith('//') ? stateReturn : '/journeys';

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getOrder(id)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        if (!result) setError('This journey could not be found. It may have been removed or access may be missing.');
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this journey'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, nonce, lastLoadedAt]);

  const receivings = overview?.receivings.filter((r) => r.orderId === id) || [];
  const current = detail?.id === id ? detail : null;
  const next = current && nextOrderAction(current, receivings, current.actions.length);
  const refresh = () => { setNonce((n) => n + 1); void load(); };
  const goTab = (next: WorkspaceTab) => navigate(`?tab=${next}`, { replace: true, state: { returnTo } });

  const saveLabel = async () => {
    setSaving(true);
    const value = label.trim() || null;
    try {
      await setOrderLabel(id, value);
      patchOrder(id, { label: value });
      setDetail((d) => d && ({ ...d, label: value }));
      setEditing(false);
      toast.success('Journey name saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save journey name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-[1500px] [&_table]:text-[13px] [&_th]:bg-muted [&_th]:font-medium [&_th]:text-muted-foreground [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5 **:data-[slot=button]:min-h-8 **:data-[slot=button]:rounded-[6px] pointer-coarse:**:data-[slot=button]:min-h-11 pointer-coarse:**:data-[slot=button]:min-w-11">
        <div className="px-4 pt-5 md:px-8">
          <Link to={returnTo} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={15} />
            Back to {returnTo === '/' || returnTo.startsWith('/?') ? 'overview' : returnTo.startsWith('/journeys') ? 'journeys' : 'work'}
          </Link>
        </div>
        <PageState loading={loading && !current} error={error} onRetry={() => setNonce((n) => n + 1)} />
        {current && next && (
          <>
            <PageHeader
              mark={current.logoUrl ? <VendorMark name={current.vendor} logoUrl={current.logoUrl} size="header" /> : undefined}
              eyebrow={`Journey #${id}`}
              title={current.vendor}
              description={current.label || undefined}
              actions={
                <JourneyActions
                  detail={current}
                  next={next}
                  onMarkOrdered={() => setMarking(true)}
                  onEditName={() => { setLabel(current.label || ''); setEditing(true); }}
                  onTab={goTab}
                  onChanged={refresh}
                  onLeft={() => { void load(); navigate(returnTo); }}
                />
              }
            />
            {editing && (
              <form className="mx-4 mb-5 flex flex-wrap gap-2 md:mx-8" onSubmit={(e) => { e.preventDefault(); void saveLabel(); }}>
                <Input aria-label="Journey name" className="max-w-md" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus disabled={saving} />
                <Button disabled={saving}><Check />{saving ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="ghost" disabled={saving} onClick={() => setEditing(false)}><X />Cancel</Button>
              </form>
            )}
            {marking && (
              <div className="mx-4 mb-5 md:mx-8">
                <MarkOrderedForm order={current} onDone={() => { setMarking(false); refresh(); }} onCancel={() => setMarking(false)} />
              </div>
            )}
            <FactsStrip detail={current} next={next} />
            <nav className="flex flex-wrap gap-1 border-b border-border px-8 max-md:px-3" aria-label="Journey sections">
              {tabs.map(([key, title]) => (
                <Link key={key} to={`?tab=${key}`} replace state={{ returnTo }} aria-current={tab === key ? 'page' : undefined} className="border-b-2 border-transparent px-[15px] py-[13px] text-[13px] text-muted-foreground aria-[current=page]:border-primary aria-[current=page]:font-semibold aria-[current=page]:text-primary max-md:px-2 max-md:py-3 pointer-coarse:min-h-11">{title}</Link>
              ))}
            </nav>
            <div className="mx-8 my-6 rounded-[10px] border border-border bg-card p-6 max-md:mx-3 max-md:my-4 max-md:p-4 **:data-[slot=button]:rounded-[6px]">
              {tab === 'summary' && <JourneyRail detail={current} receivings={receivings} onChanged={refresh} />}
              {tab === 'draft' && <OrderPrepDetail orderId={current.id} currency={current.currency} onDraftsChanged={refresh} />}
              {tab === 'items' && <ItemsTab detail={current} />}
              {tab === 'documents' && <DocumentsTab detail={current} onChanged={refresh} />}
              {tab === 'receiving' && <ReceivingTab detail={current} />}
              {tab === 'activity' && <ActivityTab detail={current} />}
              {tab === 'notes' && <NotesTab detail={current} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
